import { useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  Button,
  InlineStack,
  Paragraph,
  Popover,
  PopoverContent,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import { billing } from '@/services/billing';
import { ADDON_PRICE_IDS } from '@/services/billing/addon-change';
import { formatCentsToUsd } from '@/utils/currency';
import { useOpenActivityHistoryAddon } from '../hooks/use-open-activity-history-addon';
import { activityHistory, type ActivityHistoryKind } from '../utils/activity-history';
import { ADDONS, formatShortDateWithYear } from '../utils/addon-purchase';
import * as styles from './activity-history-chip.css';
import clsx from 'clsx';
import { FlexMark, leadingMarkButton } from '@/components/flex-mark';
import * as addonStyles from './segments-addon-button.css';

type ActivityHistoryChipProps = {
  kind: ActivityHistoryKind;
};

const ADDON_PRICE = formatCentsToUsd(ADDONS['activity-history'].priceCents, 0);

/** How far back a Flex account can see message or profile activity, and the add-on that extends it. */
export function ActivityHistoryChip({ kind }: ActivityHistoryChipProps) {
  const { account } = useAuth();
  const addOns = useStore(billing.addOns);
  const hasRetention = addOns.some((addOn) => addOn.slug === ADDON_PRICE_IDS.data_retention);
  if (!isFlexPlan(account) || hasRetention) return null;

  return <FlexActivityHistoryChip kind={kind} />;
}

function FlexActivityHistoryChip({ kind }: ActivityHistoryChipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const history = activityHistory(kind);
  const openAddon = useOpenActivityHistoryAddon();

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} withArrow={false} placement="bottom-end">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="tertiary"
          isActive={isOpen}
          elementBefore={<FlexMark />}
          data-test-activity-history-chip={kind}
        >
          History
        </Button>
      )}

      <PopoverContent className={styles.popoverContent}>
        <Stack gap="150">
          <Text text="product-p-active">{history.heading}</Text>
          <Stack gap="075">
            <InlineStack
              className={`${styles.row} ${styles.includedRow}`}
              gap="150"
              alignBlock="center"
              alignInline="space-between"
            >
              <Text text="product-psm">Included</Text>
              <Text text="product-psm-active">Back to {formatShortDateWithYear(history.lookbackStart)}</Text>
            </InlineStack>
            <InlineStack className={styles.row} gap="150" alignBlock="center" alignInline="space-between">
              <Stack>
                <Paragraph text="product-psm">
                  With data retention{' '}
                  <Text as="span" text="product-pxs-secondary">
                    {ADDON_PRICE} per billing cycle
                  </Text>
                </Paragraph>
                <Paragraph text="product-psm-active">
                  Back to {formatShortDateWithYear(history.addonLookbackStart)}
                </Paragraph>
              </Stack>
              <Button
                className={clsx(addonStyles.flexTag, leadingMarkButton)}
                size="sm"
                elementBefore={<FlexMark />}
                onClick={() => {
                  setIsOpen(false);
                  void openAddon();
                }}
              >
                Flex add-on
              </Button>
            </InlineStack>
          </Stack>
          <Paragraph text="product-pxs-secondary">
            Nothing is deleted. Add it and hidden data reappears. It also extends metrics, journeys, condition lookback
            and the audit log.
          </Paragraph>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
